/**
 * V150.27: NUCLEAR BLACK MARGIN ELIMINATION - Fixed UnifiedPhotoService
 * This service fixes the black margin issue by using crop-to-fill logic instead of fit logic
 */

import { supabase } from '../lib/supabase';
import { PROFILE_PHOTO_HEIGHT, PROFILE_PHOTO_WIDTH } from '../utils/cameraCrop';

interface PhotoMetadata {
  id: string;
  url: string;
  userId: string;
  uploadedAt: Date;
  dimensions: { width: number; height: number };
  size: number;
  format: string;
}

interface UploadProgress {
  progress: number;
  stage: string;
  message?: string;
}

// V242.3: Extended result type with publicId and preview URL
interface UploadResult {
  success: boolean;
  photoUrl?: string; // Final Cloudinary URL
  publicId?: string;
  previewUrl?: string; // Instant local preview (blob URL)
  error?: string;
}

export class FixedUnifiedPhotoService {
  private static instance: FixedUnifiedPhotoService;
  
  // Profile camera and stored output share one strict 4:5 portrait ratio.
  private readonly STANDARD_DIMENSIONS = {
    width: PROFILE_PHOTO_WIDTH,
    height: PROFILE_PHOTO_HEIGHT
  };

  private constructor() {}

  static getInstance(): FixedUnifiedPhotoService {
    if (!FixedUnifiedPhotoService.instance) {
      FixedUnifiedPhotoService.instance = new FixedUnifiedPhotoService();
    }
    return FixedUnifiedPhotoService.instance;
  }

  /**
   * V242.3: Upload photo with instant preview and background upload
   * - Shows local preview immediately (zero network wait)
   * - Uploads to Cloudinary in background
   * - Caches final image for instant future loads
   * @param userName - Optional userName to use (e.g., from onboarding context). If not provided, fetches from DB.
   * @param onPreviewReady - Callback with instant preview URL (blob URL)
   */
  async uploadPhoto(
    userId: string,
    file: File,
    onProgress?: (progress: UploadProgress) => void,
    userName?: string,
    onPreviewReady?: (previewUrl: string) => void
  ): Promise<UploadResult> {
    try {
      console.log('V242.3_UPLOAD: Starting photo upload', {
        userId,
        fileType: file?.type,
        fileSize: file?.size,
        userName,
      });

      // CRITICAL: Validate that file is a File object, not a blob URL string
      if (!userId || !file) {
        const errorMsg = `User ID and file are required. userId: ${userId}, file: ${file?.name}`;
        console.error('V242.3_UPLOAD: Validation failed:', errorMsg);
        throw new Error(errorMsg);
      }

      if (!(file instanceof File)) {
        const fileType = typeof file;
        const blobUrlPreview = fileType === 'string' ? ` (blob URL: ${(file as string).substring(0, 50)}...)` : '';
        const errorMsg = `Invalid file: expected File object, got ${fileType}${blobUrlPreview}`;
        console.error('❌ V242.3_UPLOAD: File validation failed:', errorMsg);
        throw new Error(errorMsg);
      }

      onProgress?.({ progress: 10, stage: 'validation', message: 'Validating inputs' });

      // V250: DO NOT create preview object URL here - PhotoStore will create it after resize
      // This prevents creating multiple object URLs (we only want PhotoStore's URL)

      // V150.27: Resize image with CROP-TO-FILL logic (no black bars!)
      console.log('V_UPLOAD service: uploading (pre-resize)', { userId, originalSize: file.size });
      onProgress?.({ progress: 20, stage: 'resizing', message: 'Processing image...' });
      const resizedFile = await this.resizeImageCropToFill(file);
      
      // V250: Write resized blob to PhotoStore immediately (stable key: profile_photo:userId)
      // PhotoStore.setBlob will create ONE object URL (the only allowed one for profile photos)
      let photoStoreUrl: string | null = null;
      try {
        const blob = await resizedFile.arrayBuffer().then(buf => new Blob([buf], { type: resizedFile.type || 'image/webp' }));
        const { setBlob } = await import('../lib/photoStore');
        photoStoreUrl = await setBlob(userId, blob, blob.type || 'image/webp');
        console.log('✅ V250: Resized blob written to PhotoStore (stable key, instant UI update)');
        
        // Use PhotoStore URL for preview (this is the ONLY object URL for this photo)
        if (photoStoreUrl && onPreviewReady) {
          onPreviewReady(photoStoreUrl);
        }
      } catch (photoStoreError) {
        console.error('❌ V250: PhotoStore write failed:', photoStoreError);
        // V252: Fallback - create temporary object URL for override (upload can still proceed)
        try {
          const blob = await resizedFile.arrayBuffer().then(buf => new Blob([buf], { type: resizedFile.type || 'image/webp' }));
          const fallbackUrl = URL.createObjectURL(blob);
          console.warn('⚠️ V252: PhotoStore failed, using fallback object URL for preview');
          if (onPreviewReady) {
            onPreviewReady(fallbackUrl);
          }
          // Note: This fallback URL will be replaced by remote URL after upload succeeds
        } catch (fallbackError) {
          console.error('❌ V252: Fallback preview creation also failed:', fallbackError);
          // Continue with upload anyway - remote URL will still be committed
        }
        // Don't throw - allow upload to proceed even if PhotoStore fails
      }

      onProgress?.({ progress: 30, stage: 'uploading', message: 'Uploading to cloud...' });

      // V242.1: Use signed upload directly to get publicId
      const { uploadProfilePhotoSigned, updateUserProfilePhoto } = await import('../lib/cloudinarySignedUpload');

      // V242.3: Get old photo URL BEFORE upload (for cache invalidation)
      let oldPhotoUrl: string | null = null;
      try {
        const { supabase } = await import('../integrations/supabase/client');
        const { data: userData } = await supabase
          .from('users')
          .select('photo_urls')
          .eq('id', userId)
          .maybeSingle();

        if (userData?.photo_urls && Array.isArray(userData.photo_urls) && userData.photo_urls.length > 0) {
          const firstUrl = userData.photo_urls[0];
          if (typeof firstUrl === 'string' && firstUrl.includes('res.cloudinary.com')) {
            oldPhotoUrl = firstUrl;
            console.log('📋 V242.3: Found old photo URL for invalidation:', oldPhotoUrl.substring(0, 80));
          }
        }
      } catch (err) {
        console.warn('⚠️ V242.3: Failed to get old photo URL (non-critical):', err);
      }

      // V207.20: Resolve userName (priority: provided -> DB -> localStorage -> auth metadata -> fallback)
      let finalUserName: string | undefined = userName;
      if (!finalUserName) {
        console.log('V242.3_UPLOAD: No userName provided, resolving from multiple sources');
        try {
          const { supabase } = await import('../integrations/supabase/client');
          
          // Priority 1: DB name
          const { data: userData } = await supabase
            .from('users')
            .select('name, google_name')
            .eq('id', userId)
            .maybeSingle();

          // V334-RA06: Use name only — user.name is single source of truth for display
          finalUserName = userData?.name;

          // Priority 2: localStorage (from Step1)
          if (!finalUserName) {
            const storedName = localStorage.getItem('bn_display_name');
            if (storedName) {
              finalUserName = storedName;
              console.log('V242.3_UPLOAD: Using name from localStorage:', finalUserName);
            }
          }

          // Priority 3: Auth metadata (fallback)
          if (!finalUserName) {
            const { data: { user: authUser } } = await supabase.auth.getUser();
            finalUserName = authUser?.user_metadata?.name || 
                          authUser?.user_metadata?.full_name || 
                          undefined;
          }

          console.log('V242.3_UPLOAD: Resolved userName:', finalUserName);
        } catch (err) {
          console.error('V242.3_UPLOAD: Exception resolving userName:', err);
        }
      }

      onProgress?.({ progress: 50, stage: 'uploading', message: 'Uploading to cloud...' });
      
      // V242.3: Upload to Cloudinary (background, non-blocking for UI)
      console.log('V_UPLOAD service: uploading (to Cloudinary)', { userId, resizedSize: resizedFile.size });
      const uploadResult = await uploadProfilePhotoSigned(resizedFile, finalUserName);
      const { secureUrl, publicId, version: cloudinaryVersion } = uploadResult;
      
      if (!secureUrl || !publicId) {
        throw new Error('Upload returned empty URL or publicId');
      }

      console.log('V242.3_UPLOAD: Cloudinary upload completed', {
        secureUrl: secureUrl.substring(0, 80),
        publicId,
        cloudinaryVersion,
      });
      console.log('V_UPLOAD service: cloudinary success', { urlPrefix: secureUrl.substring(0, 80), publicId });

      // V252: Store Cloudinary URL exactly as returned (no ?cv= param needed - /vTIMESTAMP/ already provides cache busting)
      // Cloudinary secure_url already includes version in path: /upload/v1766401145/...
      const finalUrl = secureUrl;

      console.log('✅ V242.3: Final URL (canonical Cloudinary URL):', {
        finalUrl: finalUrl.substring(0, 80),
        publicId,
      });

      onProgress?.({ progress: 80, stage: 'database', message: 'Saving...' });

      // V242.1: Update database with canonical Cloudinary URL and publicId
      // finalUrl is the exact secure_url from Cloudinary (includes /vTIMESTAMP/ in path for cache busting)
      await updateUserProfilePhoto(userId, finalUrl, publicId);

      onProgress?.({ progress: 90, stage: 'caching', message: 'Caching...' });

      // V243: Single source of truth update - commit to all layers at once
      // This updates globalDataStore, persistentCache, and CacheStorage
      try {
        // Convert File to Blob for caching
        const blob = await resizedFile.arrayBuffer().then(buf => new Blob([buf], { type: resizedFile.type || 'image/webp' }));
        
        const { commitProfilePhotoUpdate } = await import('./profilePhotoCommit');
        await commitProfilePhotoUpdate({
          userId,
          newUrl: finalUrl,
          publicId,
          blob,
        });
        console.log('✅ V243: Profile photo committed to all layers (instant rerender everywhere)');
        console.log('V_UPLOAD service: remote committed', { userId, urlPrefix: finalUrl.substring(0, 80) });
      } catch (commitError) {
        console.warn('⚠️ V243: Profile photo commit failed (non-critical):', commitError);
      }

      // Nuclear cache invalidation before setting new photo (legacy cleanup)
      await this.nuclearCacheInvalidation(userId);

      onProgress?.({ progress: 100, stage: 'complete', message: 'Upload complete!' });

      console.log('V243_UPLOAD: Success', { publicId, version: cloudinaryVersion, finalUrl: finalUrl.substring(0, 80) });

      // V250: PhotoStore already has the blob URL (returned from setBlob)
      // No need to return a separate previewUrl - PhotoStore is the source
      return { 
        success: true, 
        photoUrl: finalUrl, // V243: Return finalUrl (versioned) for consistency with commitProfilePhotoUpdate
        publicId,
        previewUrl: undefined, // V250: Preview comes from PhotoStore, not here
      };

    } catch (error) {
      // V250: No preview URL to clean up - PhotoStore manages it
      console.error('V242.3_UPLOAD: Photo upload failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      };
    }
  }

  /**
   * V150.27: NUCLEAR BLACK MARGIN ELIMINATION - Resize image using crop-to-fill logic
   * This is the KEY FIX - uses Math.max instead of Math.min to eliminate black bars
   */
  private async resizeImageCropToFill(file: File): Promise<File> {
    // V250: Use createImageBitmap directly - no data URLs, no object URLs
    // Canvas.drawImage supports ImageBitmap directly, so we don't need Image element
    const bitmap = await createImageBitmap(file);
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        bitmap.close();
        throw new Error('Canvas context not available');
      }

      // Set canvas to standard dimensions
      canvas.width = this.STANDARD_DIMENSIONS.width;
      canvas.height = this.STANDARD_DIMENSIONS.height;

      // V150.27: NUCLEAR BLACK MARGIN ELIMINATION - Use crop-to-fill logic
      // Calculate scaling to FILL the canvas completely (crop excess, no black bars)
      const scale = Math.max(  // ← KEY FIX: Math.max instead of Math.min
        canvas.width / bitmap.width,
        canvas.height / bitmap.height
      );

      const scaledWidth = bitmap.width * scale;
      const scaledHeight = bitmap.height * scale;

      // Center the image (this will crop excess parts)
      const x = (canvas.width - scaledWidth) / 2;
      const y = (canvas.height - scaledHeight) / 2;

      // V150.27: Fill entire canvas with black background first (optional)
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw ImageBitmap directly to canvas - NO BLACK BARS!
      // Canvas.drawImage supports ImageBitmap, no Image element needed
      ctx.drawImage(bitmap, x, y, scaledWidth, scaledHeight);

      // Convert to blob
      const resizedFile = await new Promise<File>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              console.log('✅ V250: Image resized with crop-to-fill - NO BLACK BARS, NO DATA URLS!');
              resolve(resizedFile);
            } else {
              reject(new Error('Failed to create resized image'));
            }
          },
          'image/jpeg',
          0.9 // High quality
        );
      });

      bitmap.close(); // Clean up bitmap
      return resizedFile;
    } catch (error) {
      bitmap.close(); // Clean up on error
      throw error;
    }
  }

  /**
   * Convert file to data URL for local caching
   */
  private async fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Cache photo locally for instant display
   */
  private cachePhoto(userId: string, photoUrl: string): void {
    try {
      localStorage.setItem(`photo_${userId}`, photoUrl);
      console.log('📦 V150.27: Photo cached locally for instant display');
    } catch (error) {
      console.warn('⚠️ V150.27: Failed to cache photo locally:', error);
    }
  }

  /**
   * Update database with new photo URL
   */
  private async updateDatabase(userId: string, photoUrl: string): Promise<void> {
    const { error } = await supabase
      .from('users')
      .update({ photo_urls: [photoUrl] })
      .eq('id', userId);

    if (error) {
      throw new Error(`Database update failed: ${error.message}`);
    }

    console.log('✅ V150.27: Database updated with new photo URL');
  }

  /**
   * Nuclear cache invalidation
   */
  private async nuclearCacheInvalidation(userId: string): Promise<void> {
    try {
      // Clear localStorage
      localStorage.removeItem(`photo_${userId}`);
      localStorage.removeItem(`photos_${userId}`);
      
      // Clear sessionStorage
      sessionStorage.removeItem(`photo_${userId}`);
      sessionStorage.removeItem(`photos_${userId}`);
      
      console.log('💥 V150.27: Nuclear cache invalidation completed');
    } catch (error) {
      console.warn('⚠️ V150.27: Cache invalidation warning:', error);
    }
  }

  /**
   * Get cached photo for instant display
   */
  getCachedPhoto(userId: string): string | null {
    try {
      return localStorage.getItem(`photo_${userId}`);
    } catch (error) {
      console.warn('⚠️ V150.27: Failed to get cached photo:', error);
      return null;
    }
  }
}

// Export singleton instance
export const fixedUnifiedPhotoService = FixedUnifiedPhotoService.getInstance();
